import Skeleton from '@/components/ui/Skeleton'

/**
 * Layout-mimicking skeleton for the Dashboard. Renders during the lazy
 * chunk load so the masthead → conditions strip → feed cadence is felt
 * before any data arrives.
 */
const DashboardSkeleton = () => {
  return (
    <div className="animate-fade-in">
      {/* Hero band — matches HeritageHero footprint (rounded-3xl, deep water) */}
      <section
        aria-hidden="true"
        className="relative mb-12 overflow-hidden rounded-3xl bg-water-gradient shadow-lift px-8 py-10 md:px-12 md:py-14"
      >
        <div className="max-w-2xl space-y-5">
          <Skeleton className="h-6 w-44 bg-white/15" />
          <Skeleton className="h-4 w-72 bg-white/10" />
          <Skeleton className="h-12 w-3/4 bg-white/20" />
          <div className="flex gap-3 pt-2">
            <Skeleton className="h-10 w-48 rounded-full bg-white/20" />
            <Skeleton className="h-10 w-40 rounded-full bg-white/10" />
          </div>
        </div>
      </section>

      {/* Conditions strip — 5 tiles on desktop, 2 on mobile */}
      <div className="relative -mt-6 space-y-4">
        <div className="rounded-3xl border border-mist/50 bg-white p-6 shadow-paper">
          <Skeleton className="h-4 w-32 mb-5" />
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="rounded-2xl border border-mist/40 p-4">
                <Skeleton className="h-3 w-16 mb-3" />
                <Skeleton className="h-8 w-20 mb-2" />
                <Skeleton className="h-3 w-12" />
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Upcoming events */}
      <div className="mt-12 rounded-3xl border border-mist/50 bg-white p-6 md:p-8 shadow-paper">
        <Skeleton className="h-4 w-36 mb-2" />
        <Skeleton className="h-7 w-1/2 mb-6" />
        <div className="space-y-4">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="flex gap-4">
              <Skeleton className="h-16 w-16 rounded-2xl shrink-0" />
              <div className="flex-1 space-y-2 py-1">
                <Skeleton className="h-4 w-2/3" />
                <Skeleton className="h-3 w-1/3" />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Lake cam placeholder */}
      <div className="mt-12 rounded-3xl border border-mist/50 bg-white p-6 md:p-8 shadow-paper">
        <Skeleton className="h-4 w-32 mb-4" />
        <Skeleton className="h-72 w-full rounded-2xl" />
      </div>

      {/* Heritage moment */}
      <div className="mt-20 rounded-3xl border border-mist/50 bg-white p-6 md:p-8 shadow-paper">
        <Skeleton className="h-4 w-40 mx-auto mb-3" />
        <Skeleton className="h-8 w-3/4 mx-auto mb-2" />
        <Skeleton className="h-8 w-2/3 mx-auto mb-6" />
        <div className="max-w-2xl mx-auto space-y-2.5">
          <Skeleton shape="text" className="w-full" />
          <Skeleton shape="text" className="w-full" />
          <Skeleton shape="text" className="w-4/5" />
        </div>
      </div>

      {/* Around town */}
      <div className="mt-20 rounded-3xl border border-mist/50 bg-white p-6 md:p-8 shadow-paper">
        <Skeleton className="h-4 w-36 mb-2" />
        <Skeleton className="h-7 w-1/2 mb-6" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="rounded-2xl border border-mist/40 p-5">
              <Skeleton className="h-5 w-2/3 mb-3" />
              <Skeleton shape="text" className="w-full mb-1.5" />
              <Skeleton shape="text" className="w-4/5" />
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

export default DashboardSkeleton
